import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Box,
  Typography,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { AbsenceRequestResponseDto } from "../../../types/absence";
import { DateRangeDisplay } from "../../../components/absence";
import { formatDate } from "../../../utils/absenceUtils";
import { Column, DataTable } from "../../../components/common/DataTable/DataTable";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import { useGetEmployeeAbsenceBalanceQuery } from "../../../api/absenceBalancesApi";
import { useGetOverlappingAbsenceRequestsQuery } from "../../../api/absenceRequestsApi";

interface ViewAbsenceRequestDialogProps {
  open: boolean;
  request: AbsenceRequestResponseDto | null;
  onClose: () => void;
}

export const ViewAbsenceRequestDialog: React.FC<
  ViewAbsenceRequestDialogProps
> = ({ open, request, onClose }) => {
  // Fetch employee balance data - must be called before any conditional returns
  const {
    data: balanceData,
    isLoading: isBalanceLoading,
    error: balanceError,
  } = useGetEmployeeAbsenceBalanceQuery(request?.employeeId || 0, {
    skip: !open || !request?.employeeId,
  });

  const {
    data: overlappingRequests = [],
    isLoading: isOverlappingLoading,
  } = useGetOverlappingAbsenceRequestsQuery(request?.id ?? 0, {
    skip: !open || !request?.id,
  });

  if (!request) return null;

  const categoryNames: Record<number, string> = {
    1: "Vacation",
    2: "Sick Days",
    3: "Medical Leave",
    5: "Maternity",
    6: "Parental",
    7: "Family Holiday",
  };
  const statusNames: Record<number, string> = {
    1: "Pending",
    2: "Approved",
    3: "Denied",
    4: "Cancelled",
    5: "Revoked",
  };
  const overlappingAbsenceColumns: Column[] = [
    {
      field: "employeeName",
      headerName: "Employee",
      renderCell: (value) => value ?? "-",
    },
    {
      field: "absenceCategory",
      headerName: "Type",
      renderCell: (value, row) => categoryNames[value] ?? row.absenceCategoryName,
    },
    {
      field: "startDate",
      headerName: "Dates",
      renderCell: (_value, row) => (
        <DateRangeDisplay
          startDate={row.startDate}
          endDate={row.endDate}
          days={row.daysDeducted}
          variant="inline"
          showIcon={false}
        />
      ),
    },
    {
      field: "status",
      headerName: "Status",
      renderCell: (value, row) => statusNames[value] ?? row.statusName,
    },
  ];

  const balanceType = request.absenceCategory === 1 ? 'vacation'
    : request.absenceCategory === 2 ? 'sick'
    : null;

  const InfoRow = ({
    label,
    value,
    icon,
    highlight = false,
  }: {
    label: string;
    value: React.ReactNode;
    icon?: React.ReactNode;
    highlight?: boolean;
  }) => (
    <Box sx={{ 
      p: 1.5, 
      borderRadius: 1, 
      backgroundColor: highlight ? "action.hover" : "transparent",
      border: highlight ? "1px solid" : "none",
      borderColor: highlight ? "primary.light" : "transparent",
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        backgroundColor: "action.hover",
      }
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
        {icon}
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
          {label}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 500, color: "text.primary" }}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: "1.125rem",
          fontWeight: 700,
          borderBottom: "1px solid",
          borderColor: "divider",
          pb: 2,
          color: "text.primary",
        }}
      >
        Absence Request Details
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        <Grid container spacing={2}>
          {/* Employee Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ color: "primary.main", mb: 1 }}>
              Employee Information
            </Typography>
            <Divider sx={{ mb: 1.5, borderColor: "primary.light" }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <InfoRow
              label="Employee"
              value={request.employeeName || "-"}
              icon={<PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <InfoRow
              label="Requested By"
              value={request.createdByUserName || "-"}
              icon={<PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
            />
          </Grid>

          {/* Request Details */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2, color: "primary.main" }}>
              Request Details
            </Typography>
            <Divider sx={{ mb: 1.5, borderColor: "primary.light" }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <InfoRow
              label="Absence Type"
              value={
                (() => {
                  const categoryNames = {
                    1: "Vacation",
                    2: "Sick Days", 
                    3: "Medical Leave",
                    5: "Maternity",
                    6: "Parental",
                    7: "Family Holiday"
                  };
                  const categoryColors = {
                    1: "#2196F3", // Blue
                    2: "#FF9800", // Orange
                    3: "#F44336", // Red
                    5: "#E91E63", // Pink
                    6: "#00BCD4", // Cyan
                    7: "#009688"  // Teal
                  };
                  return (
                    <span style={{ 
                      color: categoryColors[request.absenceCategory as keyof typeof categoryColors] || "#757575",
                      fontWeight: 'bold',
                      fontSize: '0.875rem'
                    }}>
                      {categoryNames[request.absenceCategory as keyof typeof categoryNames] || "Unknown"}
                    </span>
                  );
                })()
              }
              highlight={true}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <InfoRow 
              label="Status" 
              value={
                (() => {
                  const statusNames = {
                    1: "Pending",
                    2: "Approved",
                    3: "Denied",
                    4: "Cancelled",
                    5: "Revoked"
                  };
                  const statusColors = {
                    1: "#FFC107", // Yellow
                    2: "#4CAF50", // Green
                    3: "#F44336", // Red
                    4: "#9E9E9E",  // Gray
                    5: "#FF5722"   // Deep Orange
                  };
                  return (
                    <span style={{ 
                      color: statusColors[request.status as keyof typeof statusColors] || "#757575",
                      fontWeight: 'bold',
                      fontSize: '0.875rem'
                    }}>
                      {statusNames[request.status as keyof typeof statusNames] || "Unknown"}
                    </span>
                  );
                })()
              } 
              highlight={true}
            />
          </Grid>

          <Grid item xs={12}>
            <InfoRow
              label="Date Range"
              value={
                <DateRangeDisplay
                  startDate={request.startDate}
                  endDate={request.endDate}
                  days={request.daysDeducted}
                  variant="stacked"
                  showIcon={false}
                />
              }
              icon={<CalendarTodayIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
            />
          </Grid>

          <Grid item xs={12}>
            <InfoRow
              label="Reason"
              value={request.requestReason || "-"}
              icon={<DescriptionIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
            />
          </Grid>

          {request.notes && (
            <Grid item xs={12}>
              <InfoRow label="Notes" value={request.notes} />
            </Grid>
          )}

          {request.attachmentUrl && (
            <Grid item xs={12}>
              <InfoRow
                label="Attachment"
                value={
                  <Box
                    component="a"
                    href={request.attachmentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                  >
                    View Document
                  </Box>
                }
                icon={<AttachFileIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
              />
            </Grid>
          )}

          {/* Balance Information - Only show for vacation and sick day requests */}
          {balanceType && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2, color: "primary.main" }}>
                  {balanceType === 'vacation' ? 'Vacation' : 'Sick Day'} Balance
                </Typography>
                <Divider sx={{ mb: 1.5, borderColor: "primary.light" }} />
              </Grid>

              {isBalanceLoading ? (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                      Loading balance information...
                    </Typography>
                  </Box>
                </Grid>
              ) : balanceError ? (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Unable to load balance information. Please try again later.
                  </Alert>
                </Grid>
              ) : balanceData ? (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 1, 
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.paper",
                      minWidth: 120,
                    }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Available
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                        <Typography 
                          variant="h6" 
                          fontWeight={700}
                          color={
                            (balanceType === 'vacation' 
                              ? balanceData.vacationDaysAvailable + balanceData.vacationDaysCarryOver 
                              : balanceData.sickDaysAvailable) < 0 
                            ? "warning.main" 
                            : "primary.main"
                          }
                        >
                          {balanceType === 'vacation'
                            ? (balanceData.vacationDaysAvailable + balanceData.vacationDaysCarryOver).toFixed(1)
                            : balanceData.sickDaysAvailable.toFixed(1)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">days</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ 
                      p: 2, 
                      borderRadius: 1, 
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "background.paper",
                      minWidth: 120,
                    }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Used
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                        <Typography variant="h6" fontWeight={700} color="text.primary">
                          {balanceType === 'vacation'
                            ? balanceData.vacationDaysUsed.toFixed(1)
                            : balanceData.sickDaysUsed.toFixed(1)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">days</Typography>
                      </Box>
                    </Box>

                    {balanceType === 'vacation' && balanceData.vacationDaysCarryOver > 0 && (
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 1, 
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        minWidth: 120,
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                          Carry-over
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                          <Typography variant="h6" fontWeight={700} color="text.primary">
                            {balanceData.vacationDaysCarryOver.toFixed(1)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">days</Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
              ) : null}
            </>
          )}

          {/* Approval/Denial Information */}
          {(request.approvedByUserName || request.denialReason) && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2, color: "primary.main" }}>
                  Decision Details
                </Typography>
                <Divider sx={{ mb: 1.5, borderColor: "primary.light" }} />
              </Grid>

              {request.approvedByUserName && (
                <Grid item xs={12} sm={6}>
                  <InfoRow
                    label="Decided By"
                    value={request.approvedByUserName}
                    icon={<PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
                  />
                </Grid>
              )}

              {request.approvedAt && (
                <Grid item xs={12} sm={6}>
                  <InfoRow
                    label="Decision Date"
                    value={formatDate(request.approvedAt)}
                    icon={<CalendarTodayIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
                  />
                </Grid>
              )}

              {request.denialReason && (
                <Grid item xs={12}>
                  <InfoRow
                    label="Denial Reason"
                    value={request.denialReason}
                    icon={<DescriptionIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
                  />
                </Grid>
              )}

              {request.cancelledAt && (
                <Grid item xs={12} sm={6}>
                  <InfoRow
                    label="Cancelled On"
                    value={formatDate(request.cancelledAt)}
                    icon={<CalendarTodayIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
                  />
                </Grid>
              )}

              {request.revokedByUserName && (
                <Grid item xs={12} sm={6}>
                  <InfoRow
                    label="Revoked By"
                    value={request.revokedByUserName}
                    icon={<PersonIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
                  />
                </Grid>
              )}

              {request.revokedAt && (
                <Grid item xs={12} sm={6}>
                  <InfoRow
                    label="Revoked On"
                    value={formatDate(request.revokedAt)}
                    icon={<CalendarTodayIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
                  />
                </Grid>
              )}

              {request.revocationReason && (
                <Grid item xs={12}>
                  <InfoRow
                    label="Revocation Reason"
                    value={request.revocationReason}
                    icon={<DescriptionIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
                  />
                </Grid>
              )}
            </>
          )}

          {/* Overlapping Absences */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2, color: "primary.main" }}>
              Overlapping Absences
            </Typography>
            <Divider sx={{ mb: 1.5, borderColor: "primary.light" }} />
          </Grid>
          <Grid item xs={12}>
            {isOverlappingLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Loading overlapping absences...
                </Typography>
              </Box>
            ) : overlappingRequests.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No overlapping absences found.
              </Typography>
            ) : (
              <DataTable
                items={overlappingRequests}
                columns={overlappingAbsenceColumns}
                totalCount={overlappingRequests.length}
              />
            )}
          </Grid>

          {/* Timestamps */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ mt: 2, color: "primary.main" }}>
              Timeline
            </Typography>
            <Divider sx={{ mb: 1.5, borderColor: "primary.light" }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <InfoRow label="Created On" value={formatDate(request.createdAt)} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          justifyContent: "center",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          size="large"
          sx={{
            fontSize: "0.875rem",
            textTransform: "none",
            height: "40px",
            minWidth: "120px",
            fontWeight: 600,
            borderRadius: 2,
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            "&:hover": {
              boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
              transform: "translateY(-1px)",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};


